import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search, Filter } from 'lucide-react';
import { fetchBookings } from '../../lib/bookingsApi';
import BookingDetailModal from './BookingDetailModal';
import NewBookingModal    from './NewBookingModal';

const TZ = 'America/New_York';
const fmtDate = iso => new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: TZ }).format(new Date(iso));
const fmtTime = iso => new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: TZ }).format(new Date(iso));

const STATUS_OPTS = [
  { value: '',           label: 'All statuses' },
  { value: 'confirmed',  label: 'Confirmed' },
  { value: 'pending',    label: 'Pending' },
  { value: 'completed',  label: 'Completed' },
  { value: 'cancelled',  label: 'Cancelled' },
  { value: 'no_show',   label: 'No-show' },
];

const STATUS_COLOR = {
  confirmed: 'bg-emerald-100 text-emerald-700',
  pending:   'bg-amber-100 text-amber-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
  no_show:  'bg-slate-100 text-slate-600',
};

export default function Appointments() {
  const [status, setStatus]   = useState('');
  const [page, setPage]       = useState(1);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const limit = 25;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['bookings', { status, page }],
    queryFn: () => fetchBookings({ ...(status && { status }), page, limit }),
  });

  const bookings = data?.data || [];
  const total    = data?.meta?.total || 0;
  const pages    = Math.ceil(total / limit);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-semibold text-ink">Appointments</h1>
          <p className="text-sm text-ink-secondary mt-0.5">{total} total</p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn btn-primary gap-2 text-sm">
          <Plus size={15} /> New booking
        </button>
      </div>

      {/* Filters */}
      <div className="card p-3 mb-4 flex items-center gap-3">
        <Filter size={14} className="text-ink-muted shrink-0" />
        <select
          value={status}
          onChange={e => { setStatus(e.target.value); setPage(1); }}
          className="input text-sm py-1.5"
        >
          {STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-10 text-center text-ink-muted text-sm">Loading…</div>
        ) : !bookings.length ? (
          <div className="p-10 text-center text-ink-muted text-sm">No bookings found.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="border-b border-surface-border bg-surface-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-ink-muted uppercase tracking-wide">Client</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-ink-muted uppercase tracking-wide">Service</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-ink-muted uppercase tracking-wide">Staff</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-ink-muted uppercase tracking-wide">Date & Time</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-ink-muted uppercase tracking-wide">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {bookings.map(b => (
                <tr
                  key={b.id}
                  onClick={() => setSelected(b)}
                  className="hover:bg-surface-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <p className="font-medium text-ink">{b.Client?.name || '—'}</p>
                    {b.Client?.email && <p className="text-xs text-ink-muted">{b.Client.email}</p>}
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">{b.Service?.name || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {b.Staff?.color && (
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: b.Staff.color }} />
                      )}
                      <span className="text-ink-secondary">{b.Staff?.name || '—'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">
                    <p>{fmtDate(b.start_at)}</p>
                    <p className="text-xs text-ink-muted">{fmtTime(b.start_at)}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[b.status] || ''}`}>
                      {b.status?.replace('_', '-')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-surface-border">
            <p className="text-xs text-ink-muted">Page {page} of {pages}</p>
            <div className="flex gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
                className="btn btn-secondary text-xs py-1.5 px-3"
              >Prev</button>
              <button
                disabled={page === pages}
                onClick={() => setPage(p => p + 1)}
                className="btn btn-secondary text-xs py-1.5 px-3"
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <BookingDetailModal
          booking={selected}
          onClose={() => setSelected(null)}
          onRefresh={refetch}
        />
      )}
      {showNew && (
        <NewBookingModal onClose={() => { setShowNew(false); refetch(); }} />
      )}
    </div>
  );
}
